import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  FeaturedIcon,
  Icons,
  LoadingIndicator,
  useToggle,
} from '@commenda/kakashi';
import { useNotification } from '../../hooks/useNotification';
import { BaseAssignee, Filing } from '../../molecules';

interface DeleteAssigneeModalProps {
  isOpen: boolean;
  onClose: () => void;
  assignee: BaseAssignee;
  filing: Filing;
  onRemoveAssignee: (assigneeId: string) => Promise<boolean>;
  onSuccess: () => void;
  loggedInUserEmail: string;
}

const DeleteAssigneeModal = ({
  isOpen,
  onClose,
  assignee,
  filing,
  onRemoveAssignee,
  onSuccess,
  loggedInUserEmail,
}: DeleteAssigneeModalProps) => {
  const { setNotification } = useNotification();
  const isDeletingAssignee = useToggle();

  const handleDelete = async () => {
    isDeletingAssignee.on();
    const success = await onRemoveAssignee(assignee.id);

    if (!success) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Failed to delete assignee',
      });
      isDeletingAssignee.off();
      return;
    }

    setNotification({
      show: true,
      valid: true,
      title: 'Success',
      message: 'Assignee removed successfully.',
    });

    onSuccess();
    isDeletingAssignee.off();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        hideCloseIcon={isDeletingAssignee.value}
        preventCloseOnOutsideClick={isDeletingAssignee.value}
      >
        <DialogHeader className="flex justify-start items-start gap-lg border-b-0">
          <FeaturedIcon
            size="lg"
            variant="warning"
            icon={<Icons.AlertTriangle />}
          />
          <div className="flex flex-col gap-xs">
            <div className="txt-large-bold">Are you sure?</div>
            <p className="text-text-tertiary txt-small-regular">
              You are removing the assignee {assignee.name}
              {loggedInUserEmail === assignee.email ? ' (you) ' : ''} to{' '}
              {filing.filingName}. <br /> <br />
              The new assignee will receive all filing-related notifications and
              tasks for {filing.corporation.companyDisplayName}.
            </p>
          </div>
        </DialogHeader>

        <DialogFooter className="border-t-0">
          <Button
            size="sm"
            variant="secondary"
            onClick={onClose}
            disabled={isDeletingAssignee.value}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            onClick={handleDelete}
            disabled={isDeletingAssignee.value}
          >
            {isDeletingAssignee.value ? <LoadingIndicator size={16} /> : null}
            <span>Confirm</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { DeleteAssigneeModal };
